import React from 'react'
import { cn } from '@/lib/utils'

/**
 * Lightweight table loading placeholder. Renders shimmer skeleton rows so
 * list pages don't fall back to bare "Loading..." text.
 *
 * Uses the global `.shimmer-skeleton` utility (see index.css).
 */
interface TableSkeletonProps {
  rows?: number
  columns?: number
  className?: string
}

export const TableSkeleton = ({
  rows = 6,
  columns = 4,
  className,
}: TableSkeletonProps): JSX.Element => {
  return (
    <div className={cn('space-y-4', className)} aria-busy="true" aria-live="polite">
      <div className="shimmer-skeleton h-9 w-72 rounded" />
      <div className="rounded-md border overflow-hidden">
        <div className="border-b bg-muted/40 px-4 py-2.5 flex gap-4">
          {Array.from({ length: columns }).map((_, i) => (
            <div key={i} className="shimmer-skeleton h-3.5 w-24 rounded flex-1 max-w-[140px]" />
          ))}
        </div>
        <div className="divide-y">
          {Array.from({ length: rows }).map((_, r) => (
            <div key={r} className="px-4 py-3 flex gap-4">
              {Array.from({ length: columns }).map((_, c) => (
                <div
                  key={c}
                  className={cn(
                    'shimmer-skeleton h-3.5 rounded flex-1 max-w-[160px]',
                    c === 0 && 'max-w-[200px]',
                  )}
                />
              ))}
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}
